import { CartAddEvent, CartErrorEvent, CartUpdateEvent } from '@theme/events';

class AquariusTapSelector extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.form = this.closest('form');
    this.component = this.closest('product-form-component');
    if (!this.form || !this.component) return;
    this.choose = this.querySelector('[data-aq-choose]');
    this.model = this.querySelector('[data-aq-model]');
    this.message = this.querySelector('[data-aq-message]');
    if (!this.choose || !this.model || !this.message) return;
    try {
      this.variants = new Map(JSON.parse(this.dataset.variants).map(v => [String(v.id), v]));
    } catch {
      return;
    }
    this.variantId = String(this.form.elements.namedItem('id')?.value || '');
    if (!this.busy) this.available = this.variants.get(this.variantId)?.available === true && !this.addButton?.disabled;
    this.pending = false;
    this.busy = this.busy || false;
    this.form.addEventListener('submit', this.onSubmit, { capture: true, signal });
    this.addEventListener('change', this.onChoiceChange, { signal });
    const scope = this.component.closest('.shopify-section, dialog, product-card') || this.component;
    scope.addEventListener('variant:selected', this.onVariantSelected, { signal });
    scope.addEventListener('variant:update', this.onVariantUpdate, { signal });
    this.sync();
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  get addButton() {
    return this.component?.querySelector('[ref="addToCartButton"], button[name="add"]');
  }

  get isUnderSink() {
    return this.variantId === this.dataset.underSinkId;
  }

  get needsTap() {
    return this.isUnderSink && this.choose.value === '3 Way Mixer Tap';
  }

  get selectedTap() {
    const option = this.model.selectedOptions[0];
    return option && !option.disabled && /^\d+$/.test(option.value) ? option : null;
  }

  onVariantSelected = () => {
    this.pending = true;
    this.sync();
  };

  onVariantUpdate = event => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
    const variant = event.detail.resource;
    const button = event.detail.data.html?.querySelector('[ref="addToCartButton"]');
    this.variantId = variant ? String(variant.id) : '';
    this.available = variant?.available === true && !button?.disabled;
    this.pending = false;
    if (variant) this.variants.set(this.variantId, { ...this.variants.get(this.variantId), ...variant });
    queueMicrotask(() => {
      if (this.isConnected) this.sync();
    });
  };

  onChoiceChange = event => {
    if (event.target !== this.choose && event.target !== this.model) return;
    if (event.target === this.choose) this.model.value = '';
    this.message.textContent = '';
    this.model.removeAttribute('aria-invalid');
    this.sync();
  };

  sync() {
    if (!this.isUnderSink) {
      this.choose.value = 'Standard Faucet Tap';
      this.model.value = '';
    }
    const need = this.needsTap;
    this.querySelector('[data-aq-choice]').hidden = !this.isUnderSink;
    this.querySelector('[data-aq-model-wrap]').hidden = !need;
    this.choose.disabled = !this.isUnderSink || this.busy || this.pending;
    this.model.disabled = !need || this.busy || this.pending;
    this.model.required = need;
    const total = this.querySelector('[data-aq-total]');
    total.hidden = !need || !this.selectedTap || this.pending;
    this.querySelector('[data-aq-total-value]').textContent = need ? this.selectedTap?.dataset.total || '' : '';
    this.querySelector('[data-aq-note]').hidden = !need;
    if (this.addButton) {
      this.addButton.removeAttribute('on:click');
      this.addButton.disabled = !this.available || this.pending || this.busy;
      this.addButton.setAttribute('aria-busy', String(this.busy));
    }
    const accelerated = this.component.querySelector('[ref="acceleratedCheckoutButtonContainer"]');
    if (accelerated) accelerated.hidden = need || !this.available || this.pending || this.busy;
  }

  showMessage(text, failed = false) {
    this.message.setAttribute('role', failed ? 'alert' : 'status');
    this.message.setAttribute('aria-live', failed ? 'assertive' : 'polite');
    this.message.textContent = text;
  }

  onSubmit = async event => {
    if (event.target !== this.form) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.busy || this.pending) return;
    if (!this.available || String(this.form.elements.namedItem('id')?.value) !== this.variantId) {
      this.showMessage(this.dataset.unavailable, true);
      return;
    }
    const tap = this.selectedTap;
    if (this.needsTap && !tap) {
      this.model.setAttribute('aria-invalid', 'true');
      this.showMessage(this.dataset.required, true);
      this.model.focus();
      return;
    }
    const data = new FormData(this.form);
    const variant = this.variants.get(this.variantId);
    const quantity = Number(data.get('quantity') || variant?.quantity_rule?.min || this.component.dataset.quantityDefault || 1);
    const rule = variant?.quantity_rule;
    if (!Number.isSafeInteger(quantity) || quantity < (rule?.min || 1) ||
        (rule?.max && quantity > rule.max) || quantity % (rule?.increment || 1) !== 0) {
      this.showMessage(this.dataset.quantityError, true);
      return;
    }
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = /^properties\[([^\]]+)\]$/.exec(key);
      if (match && typeof value === 'string' && value !== '') properties[match[1]] = value;
      if (match && value instanceof File && value.size > 0) {
        this.showMessage(this.dataset.error, true);
        return;
      }
    }
    delete properties['Choose Tap'];
    delete properties['Tap Model'];
    if (this.isUnderSink) properties['Choose Tap'] = this.choose.value;
    if (this.needsTap) properties['Tap Model'] = tap.dataset.label;
    const main = { id: this.variantId, quantity, properties };
    const plan = data.get('selling_plan');
    if (typeof plan === 'string' && /^\d+$/.test(plan)) main.selling_plan = plan;
    const items = [main];
    if (this.needsTap) items.push({ id: tap.value, quantity, properties: { _addon: 'Tap Model' } });
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map(element => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const payload = { items };
    if (sections.length) {
      payload.sections = sections;
      const path = window.location.pathname.startsWith('/') ? window.location.pathname : '/';
      payload.sections_url = path + window.location.search;
    }
    this.busy = true;
    this.sync();
    this.showMessage(this.dataset.adding);
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart/add.js', {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      let result;
      try { result = await response.json(); } catch { throw new Error(this.dataset.error); }
      if (!response.ok || result.status) {
        throw new Error(typeof result.description === 'string' ? result.description : this.dataset.error);
      }
      this.showMessage(this.dataset.added);
      this.addButton?.closest('add-to-cart-component')?.animateAddToCart?.();
      this.querySelector('[data-aq-cart-link]').hidden = false;
      const completeSections = sections.length && sections.every(id => typeof result.sections?.[id] === 'string');
      this.component.dispatchEvent(new CartAddEvent({}, main.id, {
        source: 'product-form-component', productId: this.dataset.productId,
        variantId: main.id, itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        ...(completeSections ? { sections: result.sections } : {})
      }));
    } catch (error) {
      const reason = error instanceof TypeError ? this.dataset.error : error.message || this.dataset.error;
      const text = `${reason} ${this.dataset.review}`;
      this.showMessage(text, true);
      this.querySelector('[data-aq-cart-link]').hidden = false;
      window.dispatchEvent(new CartErrorEvent(this.component.id, text));
      try {
        const root = window.Shopify?.routes?.root || '/';
        const check = await fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (check.ok) {
          const cart = await check.json();
          if (Number.isInteger(cart.item_count)) {
            this.component.dispatchEvent(new CartUpdateEvent(cart, this.variantId, {
              source: 'aquarius-tap-selector', didError: true, itemCount: cart.item_count
            }));
          }
        }
      } catch {}
    } finally {
      this.busy = false;
      if (this.isConnected) this.sync();
    }
  };
}

if (!customElements.get('aquarius-tap-selector')) {
  customElements.define('aquarius-tap-selector', AquariusTapSelector);
}
